import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { sendMail } from '@/redux/slices/contact.slice';

import { useAppDispatch, useAppSelector } from '@/redux';
import { NS } from '@/constants';

import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import Form from '@/components/common/form/Form';
import FormField from '@/components/common/form/FormField';

import styles from './ContactForm.module.scss';
import { CONTACT_FORM_OPTIONS } from '@/constants/contact';
import { FIELD_TYPE } from '@/constants/form';

export const keyPrefix = 'noSearchResult.contactForm';

export const ContactForm = ({
  t,
  subject,
  onSubmit,
  onConfirm,
  title,
  subtitle,
  submitLabel,
  options = CONTACT_FORM_OPTIONS.DEFAULT,
  type
}) => {
  const dispatch = useAppDispatch();
  const { url: host } = useAppSelector(({ root }) => root.storeConf);
  const { isReset } = useAppSelector(({ form }) => form);
  const {
    outOfStockProductName: productName,
    outOfStockProductPartNumber: partNumber
  } = useAppSelector(({ gui }) => gui);

  const {
    css: { textAlign, fullWidth },
    fields: {
      name: { display: displayName, isRequired: isRequiredName, init: initName },
      email: { display: displayEmail, isRequired: isRequiredEmail, init: initEmail },
      telephone: { display: displayTelephone, isRequired: isRequiredTelephone, init: initTelephone },
      comment: { display: displayComment, isRequired: isRequiredComment, init: initComment }
    }
  } = options;

  const defaultValues = {
    name: initName,
    email: initEmail,
    telephone: initTelephone,
    comment: initComment
  };

  const [isResetForm, setIsResetForm] = useState(false);

  useEffect(() => {
    if (isReset) {
      setIsResetForm(isReset);
    }
  }, [isReset]);

  const handleSubmit = (data) => {
    const params = {
      name: displayName ? data?.name : initName,
      email: displayEmail ? data?.email : initEmail,
      telephone: displayTelephone ? data?.telephone : initTelephone,
      comment: displayComment ? data?.comment : initComment,
      subject,
      type,
      productName,
      partNumber
    };

    dispatch(sendMail({ t, form: params, host, options: { onSubmit, onConfirm } }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.main} style={{ textAlign }} data-test-id="main">
        {title && (
          <div className={styles.title} data-test-id="title">
            {title}
          </div>
        )}
        {subtitle && (
          <div className={styles.subtitle} data-test-id="subtitle">
            {subtitle}
          </div>
        )}
      </div>
      <Form onSubmit={handleSubmit} defaultValues={defaultValues} isReset={isResetForm} >
        {displayName &&
          <FormField
            id="contact-name"
            label={`${t('forms.lastName')} ${t('alert.and')} ${t('forms.firstName')}`}
            name="name"
            type={FIELD_TYPE.TEXT}
            isRequired={isRequiredName}
          />
        }
        {displayEmail &&
          <FormField
            id="contact-email"
            label={t('forms.email')}
            name="email"
            type={FIELD_TYPE.EMAIL}
            isRequired={isRequiredEmail}
          />
        }
        {displayTelephone &&
          <FormField
            id="contact-phone"
            label={t('forms.phone')}
            name="telephone"
            type={FIELD_TYPE.PHONE}
            isRequired={isRequiredTelephone}
          />
        }
        {displayComment &&
          <FormField
            id={'contact-comment'}
            label={t(`${keyPrefix}.textArea.label`, { ns: NS.CATEG })}
            name="comment"
            type={FIELD_TYPE.TEXT_AREA}
            isRequired={isRequiredComment}
          />
        }
        <CallToActionContainer type="submit" fullWidth={fullWidth} className="mt-4">
          {submitLabel}
        </CallToActionContainer>
      </Form>
    </div>
  );
};

const fieldShape = PropTypes.shape({
  display: PropTypes.bool,
  isRequired: PropTypes.bool,
  init: PropTypes.string
});

ContactForm.propTypes = {
  t: PropTypes.func.isRequired,
  subject: PropTypes.string,
  onSubmit: PropTypes.func,
  onConfirm: PropTypes.func,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  submitLabel: PropTypes.string,
  options: PropTypes.shape({
    css: PropTypes.shape({
      textAlign: PropTypes.oneOf(['left', 'center']),
      fullWidth: PropTypes.bool
    }),
    fields: PropTypes.shape({
      name: fieldShape,
      email: fieldShape,
      telephone: fieldShape,
      comment: fieldShape
    })
  }),
  type: PropTypes.string.isRequired
};

export default ContactForm;
